import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";

export default function SubBrandPage({ meta, children }) {
  const { tag, title, tagline, heroImg, heroVideo, overview, sections, faqs, ctaLabel, mood = "dark",
          packages, upgrades, addons, pricingNote } = meta;
  const isDark = mood === "dark";
  return (
    <div data-testid={`subbrand-${meta.slug}`} className={isDark ? "on-dark" : ""} style={{ background: isDark ? "var(--ss-bg-2)" : "var(--ss-bg)" }}>
      <section className="relative min-h-[85svh] overflow-hidden grain on-dark">
        {heroVideo ? (
          <video
            src={heroVideo}
            poster={heroImg}
            autoPlay muted loop playsInline
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <img src={heroImg} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,14,12,0.5), rgba(15,14,12,0.35) 40%, rgba(15,14,12,0.95))" }} />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-40 pb-16 min-h-[85svh] flex flex-col justify-end">
          <div className="font-label text-xs mb-5" style={{ color: "var(--ss-gold)" }}>{tag}.</div>
          <h1 className="font-display font-light tracking-tighter leading-[0.9] text-[14vw] md:text-[9vw] xl:text-[9rem]">{title}</h1>
          <p className="mt-6 font-body text-lg md:text-2xl max-w-3xl" style={{ color: "var(--ss-cream-dim)" }}>{tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={meta.ctaHref || "/book"} data-testid={`${meta.slug}-hero-cta`} className="btn-primary">{ctaLabel} <ArrowUpRight className="arrow" size={14} /></Link>
            <Link to="/availability" data-testid={`${meta.slug}-hero-avail`} className="btn-outline">Check Availability</Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Overview</div>
        </div>
        <div className="md:col-span-7">
          <Reveal>
            <p className="font-display font-light text-3xl md:text-5xl leading-tight tracking-tight">{overview}</p>
          </Reveal>
        </div>
      </section>

      {packages && packages.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-16 md:pb-20" data-testid={`${meta.slug}-packages`}>
          <div className="font-label text-xs mb-8" style={{ color: "var(--ss-rust)" }}>Packages</div>
          <div className={`grid gap-6 ${packages.length >= 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-2"}`}>
            {packages.map((p, i) => (
              <div key={p.name} data-testid={`${meta.slug}-pkg-${i}`}
                   className="p-7 border flex flex-col" style={{ borderColor: "var(--ss-border)" }}>
                <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>{p.priceLabel || "Starting at"}</div>
                <div className="font-display text-5xl mt-1" style={{ color: "var(--ss-cream)" }}>${p.price}</div>
                <div className="mt-4 font-display text-2xl" style={{ color: "var(--ss-cream)" }}>{p.name}</div>
                <div className="mt-2 text-sm" style={{ color: "var(--ss-cream-dim)" }}>{p.meta}</div>
                {p.includes && (
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.includes.map(it => (
                      <li key={it} className="flex items-start gap-2 text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                        <Check size={14} style={{ color: "var(--ss-rust)", marginTop: "3px", flexShrink: 0 }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {p.footnote && (
                  <div className="mt-5 pt-4 border-t text-xs italic" style={{ color: "var(--ss-muted)", borderColor: "var(--ss-border)" }}>
                    {p.footnote}
                  </div>
                )}
              </div>
            ))}
          </div>
          {pricingNote && (
            <p className="mt-6 text-sm text-center" style={{ color: "var(--ss-muted)" }}>{pricingNote}</p>
          )}
        </section>
      )}

      {(upgrades || addons) && (
        <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-16 md:pb-20 grid md:grid-cols-2 gap-8" data-testid={`${meta.slug}-extras`}>
          {upgrades && (
            <div className="p-7 border" style={{ borderColor: "var(--ss-border)" }}>
              <div className="font-label text-xs mb-6" style={{ color: "var(--ss-rust)" }}>Upgrades</div>
              <div className="space-y-6">
                {upgrades.map(u => (
                  <div key={u.name}>
                    <div className="flex justify-between items-baseline">
                      <div className="font-display text-2xl">{u.name}</div>
                      <div className="font-display text-2xl" style={{ color: "var(--ss-rust)" }}>{u.price}</div>
                    </div>
                    {u.availability && <div className="text-xs mt-1 italic" style={{ color: "var(--ss-muted)" }}>{u.availability}</div>}
                    {u.includes && (
                      <ul className="mt-3 space-y-1 text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                        {u.includes.map(it => <li key={it}>· {it}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {addons && (
            <div className="p-7 border" style={{ borderColor: "var(--ss-border)" }}>
              <div className="font-label text-xs mb-6" style={{ color: "var(--ss-rust)" }}>Add-Ons</div>
              <div className="space-y-4">
                {addons.map(a => (
                  <div key={a.name} className="flex justify-between items-baseline border-b pb-3" style={{ borderColor: "var(--ss-border)" }}>
                    <div className="font-body">{a.name}</div>
                    <div className="font-display text-xl" style={{ color: "var(--ss-rust)" }}>{a.price}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {children}

      {sections.map((s, i) => (
        <section key={i} className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32 grid md:grid-cols-12 gap-10 items-start" data-testid={`${meta.slug}-section-${i}`}>
          <div className="md:col-span-5">
            {s.image && (
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={s.image} alt={s.title} className="absolute inset-0 h-full w-full object-cover" />
              </div>
            )}
          </div>
          <div className="md:col-span-7">
            <div className="font-label text-xs mb-4" style={{ color: "var(--ss-rust)" }}>{String(i+1).padStart(2,'0')}</div>
            <Reveal>
              <h3 className="font-display font-light tracking-tight text-3xl md:text-5xl">{s.title}</h3>
            </Reveal>
            {s.copy && <p className="mt-5 font-body text-base md:text-lg leading-relaxed" style={{ color: "var(--ss-cream-dim)" }}>{s.copy}</p>}
            {s.items && (
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 font-body">
                {s.items.map(it => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="mt-2 h-[3px] w-3" style={{ background: "var(--ss-rust)" }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {faqs && faqs.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32" data-testid={`${meta.slug}-faqs`}>
          <div className="font-label text-xs mb-8" style={{ color: "var(--ss-rust)" }}>FAQs</div>
          <div className="divide-y" style={{ borderColor: "var(--ss-border)" }}>
            {faqs.map((f, i) => (
              <details key={i} className="group py-6 border-t" style={{ borderColor: "var(--ss-border)" }}>
                <summary className="cursor-pointer list-none flex justify-between items-center gap-6">
                  <span className="font-display text-xl md:text-3xl tracking-tight">{f.q}</span>
                  <span className="font-label text-xs text-[color:var(--ss-rust)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 font-body max-w-3xl" style={{ color: "var(--ss-cream-dim)" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="p-10 md:p-20 border" style={{ borderColor: "var(--ss-border)", background: isDark ? "var(--ss-bg)" : "var(--ss-bg-2)" }}>
          <h3 className="font-display font-light text-4xl md:text-6xl tracking-tight max-w-3xl">Ready to bring {tag.toLowerCase()} to your event?</h3>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={meta.ctaHref || "/book"} data-testid={`${meta.slug}-footer-book-btn`} className="btn-primary">{ctaLabel} <ArrowUpRight className="arrow" size={14} /></Link>
            <Link to="/gallery" className="btn-outline">See Gallery</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
